// File: /src/pages/Projects.js

import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import '../styles/CustomToastify.css';

import '../styles/Projects.css';
import '../styles/ModalStyle.css';
import ProjectsForm from '../components/ProjectsForm';
import ProjectsTable from '../components/ProjectsTable';
import apiClient from '../services/apiClient';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { useProjectsData } from '../hooks/useProjectsData';
import { useConfirmDialog } from '../hooks/useConfirmDialog';

const DEFAULT_FORM_STATE = {
	clientId: '',
	name: '',
	description: '',
	status: 'pending',
	value: '',
	due_date: null,
	start_date: null,
	end_date: null,
};

const compareValues = (aValue, bValue) => {
	if (aValue === bValue) {
		return 0;
	}

	if (aValue === null || aValue === undefined) {
		return 1;
	}

	if (bValue === null || bValue === undefined) {
		return -1;
	}

	if (typeof aValue === 'string' || typeof bValue === 'string') {
		return String(aValue).localeCompare(String(bValue), 'pt-BR', {
			numeric: true,
			sensitivity: 'base',
		});
	}

	return aValue < bValue ? -1 : 1;
};

function Projects() {
	const {
		projects,
		clients,
		loading: loadingProjects,
		error: projectsError,
		refreshProjectsData,
	} = useProjectsData();

	const { showConfirm } = useConfirmDialog();

	const [selectedProjects, setSelectedProjects] = useState([]);
	const [form, setForm] = useState(DEFAULT_FORM_STATE);
	const [editingProject, setEditingProject] = useState(null);
	const [sortConfig, setSortConfig] = useState({
		key: 'id',
		direction: 'asc',
	});
	const [showModal, setShowModal] = useState(false);

	useEffect(() => {
		if (projectsError) {
			toast.error('Erro ao carregar dados de projetos.');
		}
	}, [projectsError]);

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setForm((prev) => ({
			...prev,
			[name]:
				name === 'clientId' && value !== ''
					? String(value)
					: value ?? '',
		}));
	};

	const normalizeDateField = (dateValue) => {
		if (!dateValue) {
			return null;
		}
		if (dateValue instanceof Date) {
			return dateValue.toISOString();
		}
		const parsed = new Date(dateValue);
		return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
	};

	const addOrUpdateProject = async (e) => {
		e.preventDefault();

		if (!form.clientId) {
			toast.error('Selecione um cliente.');
			return;
		}

		try {
			const payload = {
				clientId:
					form.clientId === ''
						? ''
						: Number(form.clientId) || form.clientId,
				name: form.name,
				description: form.description ?? '',
				status: form.status,
				value: parseFloat(form.value),
				due_date: normalizeDateField(form.due_date),
				start_date: normalizeDateField(form.start_date),
				end_date: normalizeDateField(form.end_date),
			};

			if (editingProject) {
				await apiClient.put(
					API_ENDPOINTS.projectById(editingProject.id),
					payload,
					
				);
				toast.success('Projeto atualizado com sucesso!');
			} else {
				await apiClient.post(
					API_ENDPOINTS.PROJECTS,
					payload,
					
				);
				toast.success('Projeto adicionado com sucesso!');
			}

			await refreshProjectsData(true).catch(() => {});
			setSelectedProjects([]);
			resetForm();
			setShowModal(false);
		} catch (error) {
			toast.error('Erro ao salvar o projeto.');
		}
	};

	const startEditing = (project) => {
		setEditingProject(project);
		setForm({
			clientId:
				project.clientId !== undefined && project.clientId !== null
					? String(project.clientId)
					: '',
			name: project.name,
			description: project.description || '',
			status: project.status,
			value: project.value,
			due_date: project.due_date ? new Date(project.due_date) : null,
			start_date: project.start_date
				? new Date(project.start_date)
				: null,
			end_date: project.end_date ? new Date(project.end_date) : null,
		});
		setShowModal(true);
	};

	const deleteProject = async (id) => {
		const confirmed = await showConfirm({
			title: 'Excluir Projeto',
			message: 'Tem certeza que deseja excluir este projeto? Esta ação não pode ser desfeita.',
			confirmText: 'Excluir',
			cancelText: 'Cancelar',
			variant: 'danger',
		});

		if (!confirmed) {
			return;
		}

		try {
			await apiClient.delete(
				API_ENDPOINTS.projectById(id),
				
			);
			await refreshProjectsData(true).catch(() => {});
			setSelectedProjects([]);
			toast.success('Projeto excluído com sucesso!');
		} catch (error) {
			toast.error('Erro ao excluir projeto.');
		}
	};

	const handleSelectProject = (id) => {
		setSelectedProjects((prev) =>
			prev.includes(id)
				? prev.filter((projectId) => projectId !== id)
				: [...prev, id]
		);
	};

	const handleSort = (key) => {
		setSortConfig((prevConfig) => {
			const isSameKey = prevConfig.key === key;
			const newDirection =
				isSameKey && prevConfig.direction === 'asc' ? 'desc' : 'asc';

			return { key, direction: newDirection };
		});
	};

	const openModalForCreation = () => {
		resetForm();
		setShowModal(true);
	};

	const resetForm = () => {
		setForm(DEFAULT_FORM_STATE);
		setEditingProject(null);
	};

	const sortedProjects = useMemo(() => {
		if (!projects || projects.length === 0) {
			return [];
		}

		const sorted = [...projects];
		sorted.sort((a, b) => {
			const result = compareValues(a[sortConfig.key], b[sortConfig.key]);
			return sortConfig.direction === 'asc' ? result : -result;
		});

		return sorted;
	}, [projects, sortConfig]);

	const isLoading = loadingProjects && (!projects || projects.length === 0);

	return (
		<div className="projects-container">
			<h1>Gerenciamento de Projetos</h1>

			{isLoading ? (
				<p className="loading-text">
					Carregando...
					<br />
					<span className="loading-spinner"></span>
				</p>
			) : (
				<>
					<div className="action-buttons">
						<button onClick={openModalForCreation}>
							Adicionar Projeto
						</button>
					</div>

					<ProjectsTable
						projects={sortedProjects}
						handleSort={handleSort}
						sortConfig={sortConfig}
						startEditing={startEditing}
						deleteProject={deleteProject}
						selectedProjects={selectedProjects}
						onSelectProject={handleSelectProject}
					/>
				</>
			)}

			{showModal && (
				<div
					className="modal-overlay"
					onClick={(e) => {
						if (e.target.classList.contains('modal-overlay')) {
							setShowModal(false);
							resetForm();
						}
					}}
				>
					<div className="modal-content">
						<button
							className="modal-close-btn"
							onClick={() => {
								setShowModal(false);
								resetForm();
							}}
						>
							X
						</button>
						<ProjectsForm
							form={form}
							handleInputChange={handleInputChange}
							addOrUpdateProject={addOrUpdateProject}
							clients={clients ?? []}
							editingProject={editingProject}
						/>
					</div>
				</div>
			)}
		</div>
	);
}

export default Projects;
